// LibraryScreen.tsx — the article ("beat") library: a "Start here" shelf, tag
// filtering, and a flat list of compact cards. Ported from the reduced-version
// app and restyled to this app's cream/Fraunces design language.
import React from 'react';
import { Screen } from '@/components/Screen';
import { NEW_UI } from '@/config/uiFlags';
import { Display, Body } from '@/components/ui';
import { Icon } from '@/components/icons';
import { clipUrl } from '@/data/guideMessages';
import { ThemeMotif } from '@/flows/library/ThemeMotif';
import {
  LIBRARIES,
  ALL_ENTRIES,
  categoryTokens,
  entriesForTag,
  pillarColor,
  categoryKicker,
  categoryWash,
  tagLabel,
  pillarBlurb,
  isRead,
  isSaved,
  toggleSaved,
  type Article,
} from '@/services/library';

// Free-text search over the whole library — title, summary, tags and shelf — so
// results appear (and narrow) live as the user types. Body text is included too
// so a phrase inside an article still finds it.
function searchEntries(query: string): Article[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);
  const haystack = (e: Article): string =>
    [
      typeof e.title === 'string' ? e.title : '',
      e.subtext ?? '',
      (e.tags ?? []).join(' '),
      e.primaryShelf ?? '',
      typeof e.body === 'string' ? e.body : Array.isArray(e.body) ? (e.body as string[]).join(' ') : '',
    ]
      .join(' ')
      .toLowerCase();
  // An entry matches when every typed word appears somewhere in its text.
  return ALL_ENTRIES.filter((e) => {
    const h = haystack(e);
    return terms.every((t) => h.includes(t));
  });
}

function ReadingPill({ duration }: { duration: string }) {
  if (!duration) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(74,52,24,0.06)',
        color: 'var(--ink-soft)',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      <Icon name="clock" size={10} /> {duration}
    </span>
  );
}

function ReadBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(107,142,110,0.14)',
        color: 'var(--green-deep)',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      <Icon name="check" size={9} /> Read
    </span>
  );
}

function SaveButton({ id, saved }: { id: string; saved: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggleSaved(id);
      }}
      aria-label={saved ? 'Remove from saved' : 'Save for later'}
      aria-pressed={saved}
      style={{
        position: 'absolute',
        top: 7,
        right: 7,
        width: 34,
        height: 34,
        borderRadius: 999,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: saved ? 'var(--amber)' : 'var(--ink-soft)',
        opacity: saved ? 1 : 0.55,
        transition: 'transform .2s cubic-bezier(.2,.7,.3,1)',
        transform: saved ? 'scale(1.06)' : 'scale(1)',
      }}
    >
      {/* the app's heart affordance: filled when saved (matches FavouriteButton) */}
      <svg viewBox="0 0 24 24" width={18} height={18} fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
        <path d="M12 19.5C6.5 16 4 12.7 4 9.5A3.8 3.8 0 0 1 12 7a3.8 3.8 0 0 1 8 2.5c0 3.2-2.5 6.5-8 10z" />
      </svg>
    </button>
  );
}

function BeatCard({
  entry,
  compact,
  onOpen,
}: {
  entry: Article;
  compact?: boolean;
  onOpen: (id: string) => void;
}) {
  const accent = pillarColor(entry);
  const kicker = categoryKicker(entry);
  const wash = categoryWash(entry);
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: compact ? 13 : 15,
        background: `linear-gradient(180deg, ${wash}, ${wash}), linear-gradient(180deg, var(--card-top), var(--card-bot))`,
        border: '1px solid var(--hair)',
        borderLeft: `3px solid ${accent}`,
        boxShadow: 'var(--edge), var(--sh1)',
        minHeight: compact ? undefined : 104,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(entry.id)}
        style={{
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: compact ? '12px 40px 12px 14px' : '14px 40px 15px 15px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 5 : 8,
          width: '100%',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: kicker,
            }}
          >
            {entry.primaryShelf}
          </span>
          <ReadingPill duration={entry.duration} />
          {isRead(entry.id) && <ReadBadge />}
        </span>
        <span
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: compact ? 15 : 15.5,
            fontWeight: 500,
            lineHeight: 1.25,
            color: 'var(--ink)',
          }}
        >
          {entry.title}
        </span>
        {!compact && entry.subtext && (
          <span
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12.5,
              lineHeight: 1.45,
              color: 'var(--ink-soft)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {entry.subtext}
          </span>
        )}
      </button>
      <SaveButton id={entry.id} saved={isSaved(entry.id)} />
    </div>
  );
}

// A library tile — the landing is a grid of these, one per category. Tapping a
// tile opens that library's reading list. The four themes carry a Phase 2 motif;
// the situational categories (Court/Work/Health) fall back to the Phase 1 tint.
function LibraryTile({ keyName, onOpen }: { keyName: string; onOpen: () => void }) {
  const t = categoryTokens(keyName);
  const n = entriesForTag(keyName).length;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${tagLabel(keyName)} — ${n} reads`}
      style={{
        position: 'relative',
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 18,
        background: `linear-gradient(180deg, ${t.wash}, ${t.wash}), linear-gradient(180deg, var(--card-top), var(--card-bot))`,
        border: '1px solid var(--hair)',
        borderLeft: `4px solid ${t.base}`,
        boxShadow: 'var(--edge), var(--sh1)',
        padding: NEW_UI ? '13px 14px' : '16px 15px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        aspectRatio: NEW_UI ? undefined : '1 / 1',
        minHeight: NEW_UI ? 82 : undefined,
        overflow: 'hidden',
      }}
    >
      <ThemeMotif theme={keyName} opacity={0.16} />
      <span style={{ position: 'relative', zIndex: 1, fontFamily: 'Fraunces, Georgia, serif', fontSize: NEW_UI ? 16 : 18, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.18 }}>
        {tagLabel(keyName)}
      </span>
      {!NEW_UI && (
        <span style={{ position: 'relative', zIndex: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {pillarBlurb(keyName)}
        </span>
      )}
      <span style={{ position: 'relative', zIndex: 1, marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.kicker }}>
        {n} read{n === 1 ? '' : 's'} <Icon name="chevRight" size={12} />
      </span>
    </button>
  );
}

// A rounded search field with a magnifier and a clear (×) button. Filters live.
function SearchBar({ query, onChange }: { query: string; onChange: (q: string) => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginTop: 16,
        padding: '0 13px',
        height: 44,
        borderRadius: 999,
        border: '1px solid var(--hair)',
        background: 'var(--card-top)',
        boxShadow: 'var(--edge), var(--sh1)',
      }}
    >
      <span aria-hidden="true" style={{ color: 'var(--ink-soft)', display: 'flex', flexShrink: 0 }}>
        <Icon name="search" size={17} />
      </span>
      <input
        type="search"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search the library…"
        aria-label="Search the library"
        autoComplete="off"
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'var(--ink)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 15,
        }}
      />
      {query && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          style={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: 999,
            border: 'none',
            background: 'rgba(74,52,24,0.08)',
            color: 'var(--ink-soft)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            lineHeight: 1,
            paddingBottom: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

export function LibraryScreen({
  onOpenArticle,
  initialLib = null,
  onExit,
}: {
  onOpenArticle: (id: string) => void;
  initialLib?: string | null;
  /** Called when backing out of a theme the user DEEP-LINKED into (e.g. from the
      Difficulty-with-ex screen) — returns them to where they came from rather than
      the library grid. */
  onExit?: () => void;
}) {
  // null = the grid of libraries; a key = that library's reading list. initialLib
  // preselects a theme (e.g. opened from the Difficulty-with-ex screen).
  const [activeLib, setActiveLib] = React.useState<string | null>(initialLib);
  // Did the user reach the current theme from the in-screen grid (vs deep-linked in)?
  // Drives the theme back button: to the grid if so, otherwise out via onExit.
  const [cameFromGrid, setCameFromGrid] = React.useState(false);
  // Live search query (landing view only).
  const [query, setQuery] = React.useState('');
  // Re-render when read/saved state changes anywhere.
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    const h = () => force();
    window.addEventListener('renurture_library_changed', h);
    return () => window.removeEventListener('renurture_library_changed', h);
  }, []);

  // ── a single library (drill-down list) ──
  if (activeLib) {
    const t = categoryTokens(activeLib);
    const list = entriesForTag(activeLib);
    return (
      <Screen label={tagLabel(activeLib)}>
        <button
          type="button"
          onClick={() => {
            if (cameFromGrid) { setCameFromGrid(false); setActiveLib(null); }
            else if (onExit) onExit();
            else setActiveLib(null);
          }}
          aria-label={cameFromGrid ? 'Back to all libraries' : 'Back'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-soft)',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14,
            fontWeight: 600,
            padding: '4px 2px',
            minHeight: 40,
          }}
        >
          <Icon name="chevLeft" size={18} /> {cameFromGrid ? 'Libraries' : 'Back'}
        </button>
        <div style={{ borderLeft: `4px solid ${t.base}`, paddingLeft: 13, margin: '2px 0' }}>
          <Display parts={{ pre: tagLabel(activeLib), em: '' }} size={26} lh={1.14} />
          {pillarBlurb(activeLib) && (
            <Body dim size={13} style={{ marginTop: 4 }}>
              {pillarBlurb(activeLib)}
            </Body>
          )}
        </div>
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.kicker, margin: '16px 0 10px' }}>
          {list.length} read{list.length === 1 ? '' : 's'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((e) => (
            <BeatCard key={e.id} entry={e} compact onOpen={onOpenArticle} />
          ))}
        </div>
        <div style={{ height: 8 }} />
      </Screen>
    );
  }

  // ── landing: the grid of libraries ──
  return (
    <Screen label="Library">
      <div style={{ marginBottom: 6 }}>
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--amber-deep)',
          }}
        >
          Library
        </span>
      </div>
      <Display parts={{ pre: 'Read or ', em: 'listen', post: '.' }} size={NEW_UI ? 24 : 29} lh={1.14} />
      {!NEW_UI && (
        <Body dim size={13.5} style={{ marginTop: 10 }}>
          Every piece can be read or played as audio. Choose the library that meets you where you are today.
        </Body>
      )}

      {/* Search bar — filters the whole library live as you type. */}
      <SearchBar query={query} onChange={setQuery} />

      {/* When searching, show a flat list of matches and hide the category grid. */}
      {query.trim() ? (
        (() => {
          const results = searchEntries(query);
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-soft)', margin: '4px 0 10px' }}>
                {results.length} result{results.length === 1 ? '' : 's'}
              </div>
              {results.length === 0 ? (
                <Body dim size={13.5}>
                  Nothing matches “{query.trim()}”. Try a different word, or clear the search to browse by library.
                </Body>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {results.map((e) => (
                    <BeatCard key={e.id} entry={e} compact onOpen={onOpenArticle} />
                  ))}
                </div>
              )}
              <div style={{ height: 8 }} />
            </div>
          );
        })()
      ) : (
      /* Responsive grid: the tiles fill the column and gain columns as the viewport
          widens (auto-fill), so on a phone it's 2-up but on an iPad/desktop it fans
          out to 3–4 instead of staying a tiny 420px strip floating in the middle. */
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: NEW_UI ? 10 : 12, marginTop: NEW_UI ? 12 : 18 }}>
        {/* Craig illustration — top-left cell. Hidden in the compact NEW_UI grid so
            every category tile fits on one screen without scrolling. */}
        {!NEW_UI && (
        <div
          aria-hidden="true"
          style={{
            borderRadius: 18,
            border: '1px solid var(--hair)',
            boxShadow: 'var(--edge), var(--sh1)',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
          }}
        >
          <img
            src={clipUrl('reading')}
            alt=""
            decoding="async"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center bottom' }}
          />
        </div>
        )}
        {LIBRARIES.map((k) => (
          <LibraryTile key={k} keyName={k} onOpen={() => { setCameFromGrid(true); setActiveLib(k); }} />
        ))}
      </div>
      )}

      <div style={{ height: 8 }} />
    </Screen>
  );
}
